var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { addCdpSnippet, cdpQueue } from './cdp.js';
import { eventSchemas } from '../validators/index.js';
import { pruneNullValues } from '../utils.js';
const DEBUG_MODE_STORAGE_KEY = 'chordcommerce/analytics/debug';
export class ChordAnalytics {
    constructor(options) {
        var _a, _b, _c;
        /**
         * Allows snippet.js to detect whether this library has been initialized yet.
         */
        this.initialize = true;
        /**
         * Allows snippet.js to detect whether the snippet has started running yet.
         */
        this.invoked = true;
        /**
         * Set debug mode for Chord Analytics
         * @param enabled - Whether debug mode should be enabled
         */
        // eslint-disable-next-line class-methods-use-this
        this.setDebugMode = (enabled) => {
            if (window === null || window === void 0 ? void 0 : window.localStorage) {
                window.localStorage.setItem(DEBUG_MODE_STORAGE_KEY, enabled.toString());
            }
        };
        /*
         * Return the CDP instance provided in `options.cdp`.
         */
        this.cdp = () => {
            var _a;
            const a = (_a = this.options) === null || _a === void 0 ? void 0 : _a.cdp;
            if (typeof a === 'function')
                return a();
            return a;
        };
        /*
         * Return the Chord CDP instance or queue.
         */
        this.ccdp = () => {
            const { cdpDomain, cdpWriteKey, namespace } = this.options;
            if (!cdpDomain || !cdpWriteKey)
                return null;
            return (window === null || window === void 0 ? void 0 : window[`_${namespace}`]) || cdpQueue(namespace);
        };
        /*
         * Log a message to the console if `options.enableLogging` is true.
         */
        this.logger = (message, ...optionalParams) => {
            if (!this.options.enableLogging)
                return;
            // eslint-disable-next-line no-console
            console.log(message, ...optionalParams);
        };
        /**
         * Validate the body of a `track` event against the Chord tracking plan.
         */
        this.validate = (event, props) => {
            if (!event) {
                this.logger('No event name provided');
                return [{ success: false }];
            }
            const schema = event && eventSchemas[event];
            if (!schema)
                return [{ success: true, data: props }];
            return schema.map((s) => s.safeParse(props));
        };
        /**
         * Generate the event `meta` property.
         */
        this.meta = () => {
            var _a, _b, _c, _d, _e, _f;
            return Object.assign(Object.assign({}, this.options.metadata), { ownership: {
                    oms_id: (_b = (_a = this.options.metadata) === null || _a === void 0 ? void 0 : _a.ownership) === null || _b === void 0 ? void 0 : _b.omsId,
                    store_id: (_d = (_c = this.options.metadata) === null || _c === void 0 ? void 0 : _c.ownership) === null || _d === void 0 ? void 0 : _d.storeId,
                    tenant_id: (_f = (_e = this.options.metadata) === null || _e === void 0 ? void 0 : _e.ownership) === null || _f === void 0 ? void 0 : _f.tenantId,
                }, version: {
                    // TODO: make this dynamic
                    major: 3,
                    minor: 0,
                    patch: 0,
                } });
        };
        /**
         * Send a `track` event to the CDP with any event name and properties.
         */
        this.track = (event, props, options) => {
            if (!event)
                this.logger('No event name provided');
            const formattedProps = this.options.stripNull && props ? pruneNullValues(props) : props;
            const finalEventProps = Object.assign(Object.assign({}, formattedProps), { meta: this.meta() });
            if (this.options.debug) {
                this.validate(event, finalEventProps).forEach((valid) => {
                    if (!valid.success) {
                        this.logger('Chord tracking plan violation', valid.error);
                    }
                });
            }
            return new Promise((resolve) => {
                const cdpPromises = [];
                try {
                    const a = this.ccdp();
                    if (a && typeof a.track === 'function') {
                        cdpPromises.push(new Promise((cdpResolve) => {
                            a.track(event, finalEventProps, options, () => cdpResolve());
                        }));
                    }
                    const b = this.cdp();
                    if (b && typeof b.track === 'function') {
                        cdpPromises.push(new Promise((cdpResolve) => {
                            b.track(event, finalEventProps, options, () => cdpResolve());
                        }));
                    }
                    if (cdpPromises.length > 0) {
                        Promise.allSettled(cdpPromises)
                            .then(() => resolve())
                            .catch((error) => {
                            this.logger(error);
                            resolve();
                        });
                    }
                    else {
                        resolve();
                    }
                }
                catch (error) {
                    this.logger(error);
                    resolve();
                }
            });
        };
        /**
         * Send an `identify` event to the CDP with user id and traits.
         */
        this.identify = (...props) => {
            try {
                const identifyPromises = [];
                const a = this.ccdp();
                if (a && typeof a.identify === 'function') {
                    identifyPromises.push(new Promise((resolve) => {
                        a.identify(...props, () => resolve());
                    }));
                }
                const b = this.cdp();
                if (b && typeof b.identify === 'function') {
                    identifyPromises.push(new Promise((resolve) => {
                        b.identify(...props, () => resolve());
                    }));
                }
                // We don't return the promise to maintain the void return type
                Promise.allSettled(identifyPromises).then((results) => {
                    this.logger('identify event sent', results);
                });
            }
            catch (error) {
                this.logger(error);
            }
        };
        /**
         * Send a `page` event to the CDP.
         */
        this.page = () => {
            try {
                const pagePromises = [];
                const a = this.ccdp();
                if (a && typeof a.page === 'function') {
                    pagePromises.push(new Promise((resolve) => {
                        a.page({ meta: this.meta() }, () => resolve());
                    }));
                }
                const b = this.cdp();
                if (b && typeof b.page === 'function') {
                    pagePromises.push(new Promise((resolve) => {
                        b.page({ meta: this.meta() }, () => resolve());
                    }));
                }
                // We don't return the promise to maintain the void return type
                Promise.allSettled(pagePromises).then((results) => {
                    this.logger('page event sent', results);
                });
            }
            catch (error) {
                this.logger(error);
            }
        };
        this.reset = () => {
            try {
                const resetPromises = [];
                const a = this.ccdp();
                if (a && typeof a.reset === 'function') {
                    resetPromises.push(new Promise((resolve) => {
                        a.reset(() => resolve());
                    }));
                }
                const b = this.cdp();
                if (b && typeof b.reset === 'function') {
                    resetPromises.push(new Promise((resolve) => {
                        b.reset(() => resolve());
                    }));
                }
                // We don't return the promise to maintain the return type
                Promise.allSettled(resetPromises).catch(this.logger);
            }
            catch (error) {
                this.logger(error);
            }
        };
        // TODO: Add support for props.products
        this.trackCartViewed = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            const cart = (_g = (_f = (_e = (_d = this.options) === null || _d === void 0 ? void 0 : _d.formatters) === null || _e === void 0 ? void 0 : _e.objects) === null || _f === void 0 ? void 0 : _f.cart) === null || _g === void 0 ? void 0 : _g.call(_f, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const payload = {
                cart_id: cart === null || cart === void 0 ? void 0 : cart.cart_id,
                currency: cart === null || cart === void 0 ? void 0 : cart.currency,
                products: cart === null || cart === void 0 ? void 0 : cart.products,
                value: cart === null || cart === void 0 ? void 0 : cart.value,
            };
            if ((_k = (_j = (_h = this.options) === null || _h === void 0 ? void 0 : _h.formatters) === null || _j === void 0 ? void 0 : _j.events) === null || _k === void 0 ? void 0 : _k.cartViewed) {
                const formattedPayload = (_o = (_m = (_l = this.options) === null || _l === void 0 ? void 0 : _l.formatters) === null || _m === void 0 ? void 0 : _m.events) === null || _o === void 0 ? void 0 : _o.cartViewed(props, payload);
                return this.track('Cart Viewed', formattedPayload, options);
            }
            return this.track('Cart Viewed', payload, options);
        });
        // TODO: Add support for props.products
        this.trackCheckoutStarted = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _p, _q, _r, _s, _t, _u, _v;
            const formatter = (_r = (_q = (_p = this.options) === null || _p === void 0 ? void 0 : _p.formatters) === null || _q === void 0 ? void 0 : _q.events) === null || _r === void 0 ? void 0 : _r.checkoutStarted;
            const checkout = (_v = (_u = (_t = (_s = this.options) === null || _s === void 0 ? void 0 : _s.formatters) === null || _t === void 0 ? void 0 : _t.objects) === null || _u === void 0 ? void 0 : _u.checkout) === null || _v === void 0 ? void 0 : _v.call(_u, {
                checkout: props === null || props === void 0 ? void 0 : props.checkout,
            });
            const payload = checkout;
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Checkout Started', formattedPayload, options);
            }
            return this.track('Checkout Started', payload, options);
        });
        this.trackCheckoutStepCompleted = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _w, _x, _y;
            const formatter = (_y = (_x = (_w = this.options) === null || _w === void 0 ? void 0 : _w.formatters) === null || _x === void 0 ? void 0 : _x.events) === null || _y === void 0 ? void 0 : _y.checkoutStepCompleted;
            const payload = {
                checkout_id: props === null || props === void 0 ? void 0 : props.checkoutId,
                payment_method: props === null || props === void 0 ? void 0 : props.paymentMethod,
                shipping_method: props === null || props === void 0 ? void 0 : props.shippingMethod,
                step: props === null || props === void 0 ? void 0 : props.step,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Checkout Step Completed', formattedPayload, options);
            }
            return this.track('Checkout Step Completed', payload, options);
        });
        this.trackCheckoutStepViewed = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _z, _0, _1;
            const formatter = (_1 = (_0 = (_z = this.options) === null || _z === void 0 ? void 0 : _z.formatters) === null || _0 === void 0 ? void 0 : _0.events) === null || _1 === void 0 ? void 0 : _1.checkoutStepViewed;
            const payload = {
                checkout_id: props === null || props === void 0 ? void 0 : props.checkoutId,
                payment_method: props === null || props === void 0 ? void 0 : props.paymentMethod,
                shipping_method: props === null || props === void 0 ? void 0 : props.shippingMethod,
                step: props === null || props === void 0 ? void 0 : props.step,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Checkout Step Viewed', formattedPayload, options);
            }
            return this.track('Checkout Step Viewed', payload, options);
        });
        this.trackCouponApplied = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _2, _3, _4;
            const formatter = (_4 = (_3 = (_2 = this.options) === null || _2 === void 0 ? void 0 : _2.formatters) === null || _3 === void 0 ? void 0 : _3.events) === null || _4 === void 0 ? void 0 : _4.couponApplied;
            const payload = {
                cart_id: props === null || props === void 0 ? void 0 : props.cartId,
                coupon_id: props === null || props === void 0 ? void 0 : props.couponId,
                coupon_name: props === null || props === void 0 ? void 0 : props.couponName,
                discount: props === null || props === void 0 ? void 0 : props.discount,
                order_id: props === null || props === void 0 ? void 0 : props.orderId,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Coupon Applied', formattedPayload, options);
            }
            return this.track('Coupon Applied', payload, options);
        });
        this.trackCouponDenied = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _5, _6, _7;
            const formatter = (_7 = (_6 = (_5 = this.options) === null || _5 === void 0 ? void 0 : _5.formatters) === null || _6 === void 0 ? void 0 : _6.events) === null || _7 === void 0 ? void 0 : _7.couponDenied;
            const payload = {
                cart_id: props === null || props === void 0 ? void 0 : props.cartId,
                coupon_id: props === null || props === void 0 ? void 0 : props.couponId,
                coupon_name: props === null || props === void 0 ? void 0 : props.couponName,
                order_id: props === null || props === void 0 ? void 0 : props.orderId,
                reason: props === null || props === void 0 ? void 0 : props.reason,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Coupon Denied', formattedPayload, options);
            }
            return this.track('Coupon Denied', payload, options);
        });
        this.trackCouponEntered = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _8, _9, _10;
            const formatter = (_10 = (_9 = (_8 = this.options) === null || _8 === void 0 ? void 0 : _8.formatters) === null || _9 === void 0 ? void 0 : _9.events) === null || _10 === void 0 ? void 0 : _10.couponEntered;
            const payload = {
                cart_id: props === null || props === void 0 ? void 0 : props.cartId,
                coupon_id: props === null || props === void 0 ? void 0 : props.couponId,
                coupon_name: props === null || props === void 0 ? void 0 : props.couponName,
                order_id: props === null || props === void 0 ? void 0 : props.orderId,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Coupon Entered', formattedPayload, options);
            }
            return this.track('Coupon Entered', payload, options);
        });
        this.trackCouponRemoved = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _11, _12, _13;
            const formatter = (_13 = (_12 = (_11 = this.options) === null || _11 === void 0 ? void 0 : _11.formatters) === null || _12 === void 0 ? void 0 : _12.events) === null || _13 === void 0 ? void 0 : _13.couponRemoved;
            const payload = {
                cart_id: props === null || props === void 0 ? void 0 : props.cartId,
                coupon_id: props === null || props === void 0 ? void 0 : props.couponId,
                coupon_name: props === null || props === void 0 ? void 0 : props.couponName,
                discount: props === null || props === void 0 ? void 0 : props.discount,
                order_id: props === null || props === void 0 ? void 0 : props.orderId,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Coupon Removed', formattedPayload, options);
            }
            return this.track('Coupon Removed', payload, options);
        });
        this.trackEmailCaptured = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _14, _15, _16;
            const formatter = (_16 = (_15 = (_14 = this.options) === null || _14 === void 0 ? void 0 : _14.formatters) === null || _15 === void 0 ? void 0 : _15.events) === null || _16 === void 0 ? void 0 : _16.emailCaptured;
            const payload = {
                email: props === null || props === void 0 ? void 0 : props.email,
                placement_component: props === null || props === void 0 ? void 0 : props.placementComponent,
                placement_page: props === null || props === void 0 ? void 0 : props.placementPage,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Email Captured', formattedPayload, options);
            }
            return this.track('Email Captured', payload, options);
        });
        this.trackProductAdded = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28;
            const formatter = (_19 = (_18 = (_17 = this.options) === null || _17 === void 0 ? void 0 : _17.formatters) === null || _18 === void 0 ? void 0 : _18.events) === null || _19 === void 0 ? void 0 : _19.productAdded;
            const product = (_23 = (_22 = (_21 = (_20 = this.options) === null || _20 === void 0 ? void 0 : _20.formatters) === null || _21 === void 0 ? void 0 : _21.objects) === null || _22 === void 0 ? void 0 : _22.product) === null || _23 === void 0 ? void 0 : _23.call(_22, props === null || props === void 0 ? void 0 : props.product);
            const cart = (_27 = (_26 = (_25 = (_24 = this.options) === null || _24 === void 0 ? void 0 : _24.formatters) === null || _25 === void 0 ? void 0 : _25.objects) === null || _26 === void 0 ? void 0 : _26.cart) === null || _27 === void 0 ? void 0 : _27.call(_26, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const quantity = ((_28 = props === null || props === void 0 ? void 0 : props.product) === null || _28 === void 0 ? void 0 : _28.quantity) || 1;
            const payload = Object.assign(Object.assign({}, product), { cart_id: cart.cart_id, currency: cart.currency, products: [Object.assign(Object.assign({}, product), { quantity })], total: product.price * (quantity || 1), value: product.price * (quantity || 1) });
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product Added', formattedPayload, options);
            }
            return this.track('Product Added', payload, options);
        });
        this.trackProductClicked = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41;
            const formatter = (_31 = (_30 = (_29 = this.options) === null || _29 === void 0 ? void 0 : _29.formatters) === null || _30 === void 0 ? void 0 : _30.events) === null || _31 === void 0 ? void 0 : _31.productClicked;
            const product = (_35 = (_34 = (_33 = (_32 = this.options) === null || _32 === void 0 ? void 0 : _32.formatters) === null || _33 === void 0 ? void 0 : _33.objects) === null || _34 === void 0 ? void 0 : _34.product) === null || _35 === void 0 ? void 0 : _35.call(_34, props === null || props === void 0 ? void 0 : props.product);
            const cart = (_39 = (_38 = (_37 = (_36 = this.options) === null || _36 === void 0 ? void 0 : _36.formatters) === null || _37 === void 0 ? void 0 : _37.objects) === null || _38 === void 0 ? void 0 : _38.cart) === null || _39 === void 0 ? void 0 : _39.call(_38, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const quantity = ((_40 = props === null || props === void 0 ? void 0 : props.product) === null || _40 === void 0 ? void 0 : _40.quantity) || 1;
            const payload = Object.assign(Object.assign({}, product), { cart_id: cart.cart_id, currency: cart.currency, item_list_id: (_41 = props === null || props === void 0 ? void 0 : props.listId) === null || _41 === void 0 ? void 0 : _41.toString(), item_list_name: props === null || props === void 0 ? void 0 : props.listName, products: [Object.assign(Object.assign({}, product), { quantity })] });
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product Clicked', formattedPayload, options);
            }
            return this.track('Product Clicked', payload, options);
        });
        this.trackVariantClicked = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53;
            const formatter = (_44 = (_43 = (_42 = this.options) === null || _42 === void 0 ? void 0 : _42.formatters) === null || _43 === void 0 ? void 0 : _43.events) === null || _44 === void 0 ? void 0 : _44.variantClicked;
            const product = (_48 = (_47 = (_46 = (_45 = this.options) === null || _45 === void 0 ? void 0 : _45.formatters) === null || _46 === void 0 ? void 0 : _46.objects) === null || _47 === void 0 ? void 0 : _47.product) === null || _48 === void 0 ? void 0 : _48.call(_47, props === null || props === void 0 ? void 0 : props.product);
            const cart = (_52 = (_51 = (_50 = (_49 = this.options) === null || _49 === void 0 ? void 0 : _49.formatters) === null || _50 === void 0 ? void 0 : _50.objects) === null || _51 === void 0 ? void 0 : _51.cart) === null || _52 === void 0 ? void 0 : _52.call(_51, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const quantity = ((_53 = props === null || props === void 0 ? void 0 : props.product) === null || _53 === void 0 ? void 0 : _53.quantity) || 1;
            const payload = Object.assign(Object.assign({}, product), { cart_id: cart.cart_id, currency: cart.currency, quantity: quantity, line_item_id: props === null || props === void 0 ? void 0 : props.lineItemId, coupon: props === null || props === void 0 ? void 0 : props.coupon });
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Variant Clicked', formattedPayload, options);
            }
            return this.track('Variant Clicked', payload, options);
        });
        this.trackProductListFiltered = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _54, _55, _56, _57, _58;
            const formatter = (_56 = (_55 = (_54 = this.options) === null || _54 === void 0 ? void 0 : _54.formatters) === null || _55 === void 0 ? void 0 : _55.events) === null || _56 === void 0 ? void 0 : _56.productListFiltered;
            const payload = {
                category: props === null || props === void 0 ? void 0 : props.category,
                filters: props === null || props === void 0 ? void 0 : props.filters,
                list_id: (_57 = props === null || props === void 0 ? void 0 : props.listId) === null || _57 === void 0 ? void 0 : _57.toString(),
                item_list_id: (_58 = props === null || props === void 0 ? void 0 : props.listId) === null || _58 === void 0 ? void 0 : _58.toString(),
                item_list_name: props === null || props === void 0 ? void 0 : props.listName,
                sorts: props === null || props === void 0 ? void 0 : props.sorts,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product List Filtered', formattedPayload, options);
            }
            return this.track('Product List Filtered', payload, options);
        });
        this.trackProductListViewed = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _59, _60, _61, _62, _63, _64;
            const formatter = (_61 = (_60 = (_59 = this.options) === null || _59 === void 0 ? void 0 : _59.formatters) === null || _60 === void 0 ? void 0 : _60.events) === null || _61 === void 0 ? void 0 : _61.productListViewed;
            const payload = {
                category: props === null || props === void 0 ? void 0 : props.category,
                list_id: (_62 = props === null || props === void 0 ? void 0 : props.listId) === null || _62 === void 0 ? void 0 : _62.toString(),
                item_list_id: (_63 = props === null || props === void 0 ? void 0 : props.listId) === null || _63 === void 0 ? void 0 : _63.toString(),
                item_list_name: props === null || props === void 0 ? void 0 : props.listName,
                products: (_64 = props === null || props === void 0 ? void 0 : props.products) === null || _64 === void 0 ? void 0 : _64.map((p, i) => {
                    var _a, _b, _c, _d;
                    return (_d = (_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.formatters) === null || _b === void 0 ? void 0 : _b.objects) === null || _c === void 0 ? void 0 : _c.product) === null || _d === void 0 ? void 0 : _d.call(_c, Object.assign(Object.assign({}, p), { position: i + 1 }));
                }),
                value: 0, // TODO: remove from tracking plan
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product List Viewed', formattedPayload, options);
            }
            return this.track('Product List Viewed', payload, options);
        });
        this.trackProductRemoved = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75, _76, _77, _78, _79;
            const formatter = (_67 = (_66 = (_65 = this.options) === null || _65 === void 0 ? void 0 : _65.formatters) === null || _66 === void 0 ? void 0 : _66.events) === null || _67 === void 0 ? void 0 : _67.productRemoved;
            /*
            const product = this.options?.formatters?.objects?.product?.({
              position: props?.position,
              product: props?.product,
              quantity: props?.quantity,
              variantId: props?.variantId,
            })
            */
            const lineItem = (_71 = (_70 = (_69 = (_68 = this.options) === null || _68 === void 0 ? void 0 : _68.formatters) === null || _69 === void 0 ? void 0 : _69.objects) === null || _70 === void 0 ? void 0 : _70.lineItem) === null || _71 === void 0 ? void 0 : _71.call(_70, {
                lineItem: props === null || props === void 0 ? void 0 : props.lineItem,
            });
            const cart = (_75 = (_74 = (_73 = (_72 = this.options) === null || _72 === void 0 ? void 0 : _72.formatters) === null || _73 === void 0 ? void 0 : _73.objects) === null || _74 === void 0 ? void 0 : _74.cart) === null || _75 === void 0 ? void 0 : _75.call(_74, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const payload = Object.assign(Object.assign({}, lineItem), { cart_id: cart.cart_id, currency: cart.currency, products: [lineItem], total: ((_76 = lineItem === null || lineItem === void 0 ? void 0 : lineItem.price) !== null && _76 !== void 0 ? _76 : 0) * ((_77 = lineItem === null || lineItem === void 0 ? void 0 : lineItem.quantity) !== null && _77 !== void 0 ? _77 : 1), value: ((_78 = lineItem === null || lineItem === void 0 ? void 0 : lineItem.price) !== null && _78 !== void 0 ? _78 : 0) * ((_79 = lineItem === null || lineItem === void 0 ? void 0 : lineItem.quantity) !== null && _79 !== void 0 ? _79 : 1) });
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product Removed', formattedPayload, options);
            }
            return this.track('Product Removed', payload, options);
        });
        this.trackProductViewed = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _80, _81, _82, _83, _84, _85, _86, _87, _88, _89, _90, _91, _92;
            const formatter = (_82 = (_81 = (_80 = this.options) === null || _80 === void 0 ? void 0 : _80.formatters) === null || _81 === void 0 ? void 0 : _81.events) === null || _82 === void 0 ? void 0 : _82.productViewed;
            const product = (_86 = (_85 = (_84 = (_83 = this.options) === null || _83 === void 0 ? void 0 : _83.formatters) === null || _84 === void 0 ? void 0 : _84.objects) === null || _85 === void 0 ? void 0 : _85.product) === null || _86 === void 0 ? void 0 : _86.call(_85, props === null || props === void 0 ? void 0 : props.product);
            const cart = (_90 = (_89 = (_88 = (_87 = this.options) === null || _87 === void 0 ? void 0 : _87.formatters) === null || _88 === void 0 ? void 0 : _88.objects) === null || _89 === void 0 ? void 0 : _89.cart) === null || _90 === void 0 ? void 0 : _90.call(_89, {
                cart: props === null || props === void 0 ? void 0 : props.cart,
            });
            const quantity = ((_91 = props === null || props === void 0 ? void 0 : props.product) === null || _91 === void 0 ? void 0 : _91.quantity) || 1;
            const payload = Object.assign(Object.assign({}, product), { cart_id: cart.cart_id, currency: cart.currency, products: [Object.assign(Object.assign({}, product), { quantity })], value: product.price * (((_92 = props === null || props === void 0 ? void 0 : props.product) === null || _92 === void 0 ? void 0 : _92.quantity) || 1) });
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Product Viewed', formattedPayload, options);
            }
            return this.track('Product Viewed', payload, options);
        });
        this.trackProductsSearched = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _93, _94, _95, _96;
            const formatter = (_95 = (_94 = (_93 = this.options) === null || _93 === void 0 ? void 0 : _93.formatters) === null || _94 === void 0 ? void 0 : _94.events) === null || _95 === void 0 ? void 0 : _95.productsSearched;
            const payload = {
                currency: props === null || props === void 0 ? void 0 : props.currency,
                price: typeof (props === null || props === void 0 ? void 0 : props.price) !== 'undefined' ? Number(props === null || props === void 0 ? void 0 : props.price) : undefined,
                product_id: (_96 = props === null || props === void 0 ? void 0 : props.productId) === null || _96 === void 0 ? void 0 : _96.toString(),
                quantity: props === null || props === void 0 ? void 0 : props.quantity,
                query: props === null || props === void 0 ? void 0 : props.query,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Products Searched', formattedPayload, options);
            }
            return this.track('Products Searched', payload, options);
        });
        this.trackSignedIn = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _97, _98, _99;
            const formatter = (_99 = (_98 = (_97 = this.options) === null || _97 === void 0 ? void 0 : _97.formatters) === null || _98 === void 0 ? void 0 : _98.events) === null || _99 === void 0 ? void 0 : _99.signedIn;
            const payload = {
                email: props === null || props === void 0 ? void 0 : props.email,
                method: props === null || props === void 0 ? void 0 : props.method,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Signed In', formattedPayload, options);
            }
            return this.track('Signed In', payload, options);
        });
        this.trackSignedOut = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _100, _101, _102;
            const formatter = (_102 = (_101 = (_100 = this.options) === null || _100 === void 0 ? void 0 : _100.formatters) === null || _101 === void 0 ? void 0 : _101.events) === null || _102 === void 0 ? void 0 : _102.signedOut;
            const payload = {
                email: props === null || props === void 0 ? void 0 : props.email,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props || {}, payload);
                return this.track('Signed Out', formattedPayload, options);
            }
            return this.track('Signed Out', payload, options);
        });
        this.trackSignedUp = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _103, _104, _105;
            const formatter = (_105 = (_104 = (_103 = this.options) === null || _103 === void 0 ? void 0 : _103.formatters) === null || _104 === void 0 ? void 0 : _104.events) === null || _105 === void 0 ? void 0 : _105.signedUp;
            const payload = {
                email: props === null || props === void 0 ? void 0 : props.email,
                method: props === null || props === void 0 ? void 0 : props.method,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props || {}, payload);
                return this.track('Signed Up', formattedPayload, options);
            }
            return this.track('Signed Up', payload, options);
        });
        this.trackLoginStarted = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _106, _107, _108;
            const formatter = (_108 = (_107 = (_106 = this.options) === null || _106 === void 0 ? void 0 : _106.formatters) === null || _107 === void 0 ? void 0 : _107.events) === null || _108 === void 0 ? void 0 : _108.loginStarted;
            const payload = {
                email: props === null || props === void 0 ? void 0 : props.email,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Login Started', formattedPayload, options);
            }
            return this.track('Login Started', payload, options);
        });
        this.trackSubscriptionCancelled = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _109, _110, _111, _112;
            const formatter = (_111 = (_110 = (_109 = this.options) === null || _109 === void 0 ? void 0 : _109.formatters) === null || _110 === void 0 ? void 0 : _110.events) === null || _111 === void 0 ? void 0 : _111.subscriptionCancelled;
            const payload = {
                actionable_date: props === null || props === void 0 ? void 0 : props.actionableDate,
                address: props === null || props === void 0 ? void 0 : props.address,
                brand: props === null || props === void 0 ? void 0 : props.brand,
                email: props === null || props === void 0 ? void 0 : props.email,
                end_date: props === null || props === void 0 ? void 0 : props.endDate,
                interval_length: props === null || props === void 0 ? void 0 : props.intervalLength,
                interval_units: props === null || props === void 0 ? void 0 : props.intervalUnits,
                products: (_112 = props === null || props === void 0 ? void 0 : props.products) === null || _112 === void 0 ? void 0 : _112.map((p, i) => {
                    var _a, _b, _c, _d;
                    return (_d = (_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.formatters) === null || _b === void 0 ? void 0 : _b.objects) === null || _c === void 0 ? void 0 : _c.product) === null || _d === void 0 ? void 0 : _d.call(_c, Object.assign(Object.assign({}, p), { position: i + 1 }));
                }),
                state: props === null || props === void 0 ? void 0 : props.state,
                subscription_id: props === null || props === void 0 ? void 0 : props.subscriptionId,
                currency: props === null || props === void 0 ? void 0 : props.currency,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props || {}, payload);
                return this.track('Subscription Cancelled', formattedPayload, options);
            }
            return this.track('Subscription Cancelled', payload, options);
        });
        this.trackNavigationClicked = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _113, _114, _115;
            const formatter = (_115 = (_114 = (_113 = this.options) === null || _113 === void 0 ? void 0 : _113.formatters) === null || _114 === void 0 ? void 0 : _114.events) === null || _115 === void 0 ? void 0 : _115.navigationClicked;
            const payload = {
                category: props === null || props === void 0 ? void 0 : props.category,
                label: props === null || props === void 0 ? void 0 : props.label,
                navigation_placement: props === null || props === void 0 ? void 0 : props.navigationPlacement,
                navigation_title: props === null || props === void 0 ? void 0 : props.navigationTitle,
                navigation_url: props === null || props === void 0 ? void 0 : props.navigationUrl,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Navigation Clicked', formattedPayload, options);
            }
            return this.track('Navigation Clicked', payload, options);
        });
        this.trackPaymentInfoEntered = (props, options) => __awaiter(this, void 0, void 0, function* () {
            var _116, _117, _118, _119;
            const formatter = (_118 = (_117 = (_116 = this.options) === null || _116 === void 0 ? void 0 : _116.formatters) === null || _117 === void 0 ? void 0 : _117.events) === null || _118 === void 0 ? void 0 : _118.paymentInfoEntered;
            const payload = {
                checkout_id: props === null || props === void 0 ? void 0 : props.checkoutId,
                coupon: props === null || props === void 0 ? void 0 : props.coupon,
                currency: props === null || props === void 0 ? void 0 : props.currency,
                order_id: props === null || props === void 0 ? void 0 : props.orderId,
                payment_method: props === null || props === void 0 ? void 0 : props.paymentMethod,
                products: (_119 = props === null || props === void 0 ? void 0 : props.products) === null || _119 === void 0 ? void 0 : _119.map((p, i) => {
                    var _a, _b, _c, _d;
                    return (_d = (_c = (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.formatters) === null || _b === void 0 ? void 0 : _b.objects) === null || _c === void 0 ? void 0 : _c.product) === null || _d === void 0 ? void 0 : _d.call(_c, Object.assign(Object.assign({}, p), { position: i + 1 }));
                }),
                shipping_method: props === null || props === void 0 ? void 0 : props.shippingMethod,
                step: props === null || props === void 0 ? void 0 : props.step,
                value: props === null || props === void 0 ? void 0 : props.value,
            };
            if (typeof formatter === 'function') {
                const formattedPayload = formatter(props, payload);
                return this.track('Payment Info Entered', formattedPayload, options);
            }
            return this.track('Payment Info Entered', payload, options);
        });
        const debug = ((_a = window === null || window === void 0 ? void 0 : window.localStorage) === null || _a === void 0 ? void 0 : _a.getItem(DEBUG_MODE_STORAGE_KEY)) === 'true';
        const defaults = {
            debug,
            enableLogging: debug,
            namespace: 'chord',
            stripNull: true,
            awaitConsent: true,
        };
        this.options = Object.assign(Object.assign({}, defaults), options);
        if (((_b = this.options) === null || _b === void 0 ? void 0 : _b.cdpDomain) && ((_c = this.options) === null || _c === void 0 ? void 0 : _c.cdpWriteKey) && window) {
            window[this.options.namespace] = (window === null || window === void 0 ? void 0 : window[this.options.namespace]) || this;
            addCdpSnippet(this.options);
        }
    }
}
